import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherHelpCircle, FeatherDownload, FeatherLoader, FeatherUpload, FeatherFileText, FeatherCheckCircle, FeatherXCircle, FeatherChevronDown, FeatherChevronUp } from "@subframe/core";
import { getUniqueL2Leads, downloadRegisterTemplate, uploadRegister, L2Lead, UploadRegisterResponse, UploadError } from "../../data/api/registersApi";

export const UploadRegistersPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();

  // State
  const [l2Leads, setL2Leads] = useState<L2Lead[]>([]);
  const [selectedL2Id, setSelectedL2Id] = useState<number | null>(null);
  const [isLoadingL2Leads, setIsLoadingL2Leads] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string>("");
  const [downloadSuccess, setDownloadSuccess] = useState<string>("");
  
  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<UploadRegisterResponse | null>(null);
  const [showUploadErrors, setShowUploadErrors] = useState<boolean>(false);

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Load L2 leads on mount
  useEffect(() => {
    loadL2Leads();
  }, []);

  // Load unique L2 leads
  const loadL2Leads = async (): Promise<void> => {
    setIsLoadingL2Leads(true);
    setDownloadError("");
    try {
      const response = await getUniqueL2Leads();
      setL2Leads(response.data || []);
    } catch (err: any) {
      console.error("Failed to load L2 leads:", err);
      setDownloadError(`Failed to load L2 leads: ${err.message || "Unknown error"}`);
    } finally {
      setIsLoadingL2Leads(false);
    }
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/cfo/dashboard/queries");
  };

  // Handle L2 selection change
  const handleL2Change = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;
    setSelectedL2Id(value ? parseInt(value, 10) : null);
    setDownloadError("");
    setDownloadSuccess("");
  };

  // Handle download template
  const handleDownloadTemplate = async (): Promise<void> => {
    if (!selectedL2Id) {
      setDownloadError("Please select an L2 lead first.");
      return;
    }

    setDownloadError("");
    setDownloadSuccess("");
    setIsDownloading(true);

    try {
      await downloadRegisterTemplate(selectedL2Id);
      const selectedL2 = l2Leads.find((lead) => lead.id === selectedL2Id);
      setDownloadSuccess(`Template downloaded successfully for "${selectedL2?.level_value || "selected L2"}"`);
    } catch (err: any) {
      console.error("Failed to download template:", err);
      setDownloadError(err.message || "Failed to download template. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedUploadFile(file);
      setUploadResult(null);
      setShowUploadErrors(false);
    }
  };

  // Handle upload (validation phase)
  const handleUpload = async (): Promise<void> => {
    if (!selectedUploadFile) return;

    setIsUploading(true);
    setUploadResult(null);
    setShowUploadErrors(false);

    try {
      const result = await uploadRegister(selectedUploadFile, "validation");
      setUploadResult(result);
    } catch (err: any) {
      console.error("Upload error:", err);
      setUploadResult({
        success: false,
        phase: "validation",
        message: err?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle confirm upload
  const handleConfirmUpload = async (): Promise<void> => {
    if (!selectedUploadFile || !uploadResult) return;

    setIsUploading(true);

    try {
      const result = await uploadRegister(selectedUploadFile, "confirm");
      setUploadResult(result);
      
      if (result.success) {
        setSelectedUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        // Clear result after a delay
        setTimeout(() => {
          setUploadResult(null);
        }, 5000);
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      setUploadResult({
        success: false,
        phase: "uploaded",
        message: err?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherHelpCircle />}
              onClick={handleRaiseQuery}
            >
              Raise Query
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          <div className="flex w-full h-full flex-col items-start p-8 gap-6">
            <div className="text-heading-1 font-heading-1 text-default-font">
              Upload Registers
            </div>
            
            <div className="flex w-full max-w-4xl flex-col items-start gap-6">
              {/* Template Download Section */}
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Download Register Template
                </span>
                
                <div className="flex w-full flex-col gap-4 p-6 rounded-md border border-solid border-neutral-border bg-default-background">
                  {/* L2 Selection */}
                  <div className="flex flex-col gap-2">
                    <label className="text-body font-body text-default-font">
                      Select L2 Lead
                    </label>
                    {isLoadingL2Leads ? (
                      <div className="flex items-center gap-2 text-body font-body text-subtext-color">
                        <FeatherLoader className="animate-spin" />
                        <span>Loading L2 leads...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedL2Id || ""}
                        onChange={handleL2Change}
                        className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        disabled={l2Leads.length === 0}
                      >
                        <option value="">-- Select an L2 Lead --</option>
                        {l2Leads.map((lead) => (
                          <option key={lead.id} value={lead.id}>
                            {lead.level_value}
                          </option>
                        ))}
                      </select>
                    )}
                    {l2Leads.length === 0 && !isLoadingL2Leads && (
                      <span className="text-caption font-caption text-subtext-color">
                        No L2 leads found for this company.
                      </span>
                    )}
                  </div>

                  {/* Error/Success Messages */}
                  {downloadError && (
                    <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
                      {downloadError}
                    </div>
                  )}
                  {downloadSuccess && (
                    <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-md text-body font-body text-green-700">
                      {downloadSuccess}
                    </div>
                  )}

                  {/* Download Button */}
                  <Button
                    variant="brand-primary"
                    onClick={handleDownloadTemplate}
                    disabled={isDownloading || !selectedL2Id || isLoadingL2Leads}
                    icon={isDownloading ? <FeatherLoader className="animate-spin" /> : <FeatherDownload />}
                  >
                    {isDownloading ? "Downloading Template..." : "Download Template"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Upload Register Template Section */}
            <div className="flex w-full flex-col items-start gap-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Upload Register Template
              </span>
              
              <div className="flex w-full flex-col gap-4 p-6 rounded-md border border-solid border-neutral-border bg-default-background">
                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />

                {/* File Selection Button */}
                <div className="flex w-full items-center gap-4">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherUpload />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {selectedUploadFile ? "Change File" : "Select File"}
                  </Button>
                  
                  {selectedUploadFile && (
                    <div className="flex items-center gap-2">
                      <FeatherFileText className="text-body font-body text-subtext-color" />
                      <span className="text-body font-body text-default-font">
                        {selectedUploadFile.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        ({(selectedUploadFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {selectedUploadFile && (
                  <div className="flex w-full items-center justify-end">
                    <Button
                      variant="brand-primary"
                      icon={isUploading ? <FeatherLoader className="animate-spin" /> : <FeatherUpload />}
                      onClick={handleUpload}
                      disabled={isUploading || !selectedUploadFile}
                    >
                      {isUploading ? "Validating..." : "Validate & Upload"}
                    </Button>
                  </div>
                )}

                {/* Help Text */}
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Supported formats: XLSX, XLS
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Maximum file size: 10MB
                  </span>
                </div>
              </div>
            </div>

            {/* Upload Results */}
            {uploadResult && (
              <div className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid p-6 ${
                uploadResult.success 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    {uploadResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-body-bold font-body-bold ${
                        uploadResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {uploadResult.success 
                          ? (uploadResult.phase === 'validation' ? 'Validation Successful' : 'Upload Successful')
                          : 'Validation Failed'}
                      </span>
                      <span className={`text-caption font-caption ${
                        uploadResult.success ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {uploadResult.message}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="flex w-full gap-4">
                  <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                    <span className="text-caption font-caption text-subtext-color">Total Rows</span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {uploadResult.data.total_rows}
                    </span>
                  </div>
                  
                  {uploadResult.phase === 'validation' && uploadResult.success && (
                    <>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Valid Rows</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {uploadResult.data.valid_rows || 0}
                        </span>
                      </div>
                      {uploadResult.data.skipped_rows !== undefined && uploadResult.data.skipped_rows > 0 && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-yellow-300">
                          <span className="text-caption font-caption text-subtext-color">Skipped Rows</span>
                          <span className="text-heading-3 font-heading-3 text-yellow-600">
                            {uploadResult.data.skipped_rows}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Invalid Rows</span>
                        <span className="text-heading-3 font-heading-3 text-red-600">
                          {uploadResult.data.invalid_rows || 0}
                        </span>
                      </div>
                    </>
                  )}

                  {uploadResult.phase === 'uploaded' && uploadResult.success && (
                    <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                      <span className="text-caption font-caption text-subtext-color">Uploaded</span>
                      <span className="text-heading-3 font-heading-3 text-green-600">
                        {uploadResult.data.uploaded || 0}
                      </span>
                    </div>
                  )}
                </div>

                {/* Skipped Rows List */}
                {uploadResult.data.skipped_preview && uploadResult.data.skipped_preview.length > 0 && (
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-body-bold font-body-bold text-yellow-900">
                        Skipped Rows ({uploadResult.data.skipped_rows || 0})
                      </span>
                      <span className="text-caption font-caption text-yellow-700">
                        These rows were skipped because the ledger names were not found in the database for this L2 level.
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                      {uploadResult.data.skipped_preview.map((skipped: { row: number; ledger_name: string; reason: string }, index: number) => (
                        <div
                          key={index}
                          className="flex flex-col gap-1 p-3 rounded-md bg-white border border-solid border-yellow-200"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-body-bold font-body-bold text-yellow-900">
                              Row {skipped.row}
                            </span>
                            <span className="text-body font-body text-subtext-color">
                              - {skipped.ledger_name}
                            </span>
                          </div>
                          <span className="text-body font-body text-yellow-700">
                            {skipped.reason}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error List */}
                {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                  <div className="flex w-full flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setShowUploadErrors(!showUploadErrors)}
                      className="flex items-center justify-between text-body font-body text-default-font cursor-pointer hover:text-brand-600 transition-colors"
                    >
                      <span>View Errors ({uploadResult.data.errors.length})</span>
                      {showUploadErrors ? <FeatherChevronUp /> : <FeatherChevronDown />}
                    </button>
                    
                    {showUploadErrors && (
                      <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                        {uploadResult.data.errors.map((error: UploadError, index: number) => (
                          <div
                            key={index}
                            className="flex flex-col gap-1 p-3 rounded-md bg-white border border-solid border-red-200"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-body-bold font-body-bold text-red-900">
                                Row {error.row}
                              </span>
                              {error.ledger_name && (
                                <span className="text-body font-body text-subtext-color">
                                  - {error.ledger_name}
                                </span>
                              )}
                            </div>
                            {error.field && (
                              <span className="text-caption font-caption text-subtext-color">
                                Field: {error.field}
                              </span>
                            )}
                            <span className="text-body font-body text-red-700">
                              {error.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Confirm Upload Button */}
                {uploadResult.success && uploadResult.phase === 'validation' && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={isUploading ? <FeatherLoader className="animate-spin" /> : <FeatherUpload />}
                      onClick={handleConfirmUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Confirm & Upload"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

